import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";

import { GooseGameQuestType } from "src/api/types.generated";
import { errorToast, successToast } from "src/shared/components";
import { EVENT_ID } from "src/shared/constants/event";

import QuestCard from "../components/cards/QuestCard";
import MarkModal from "../components/modals/organizer/hacker_scan/mark";
import { useAdjustGooseGamePlayerPointsMutation } from "../graphql/adjustGooseGamePlayerPoints.generated";
import { useGetGooseGamesQuestsByTypeQuery } from "../graphql/getGooseGameQuestsByType.generated";
import { GGPlayer, Quest, QuestLog, QuestType } from "../types";

import { useDeleteGooseGameQuestLogMutation } from "./graphql/deleteGooseGameQuestLog.generated";
import { useGetGooseGameQuestLogsQuery } from "./graphql/getGooseGameQuestLogs.generated";
import { useSubmitGooseGameOrganizerQuestLogMutation } from "./graphql/submitGooseGameOrganizerQuestLog.generated";

interface QuestManagementProps {
  GooseGamePlayer: GGPlayer;
}
const QuestManagement: React.FC<QuestManagementProps> = ({
  GooseGamePlayer,
}) => {
  const { data: questData } = useGetGooseGamesQuestsByTypeQuery({
    variables: {
      event_id: EVENT_ID,
      type: GooseGameQuestType.OrganizerApproved,
    },
  });

  const { data: questLogData, refetch } = useGetGooseGameQuestLogsQuery({
    variables: { event_attendee_id: GooseGamePlayer.event_attendee_id },
  });

  const [submitCompleteLog] = useSubmitGooseGameOrganizerQuestLogMutation();
  const [submitIncompleteLog] = useDeleteGooseGameQuestLogMutation();
  const [updatePlayerPoints] = useAdjustGooseGamePlayerPointsMutation();

  const [quests, setQuests] = useState<Quest[]>([]);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [activeQuest, setActiveQuest] = useState<Quest | null>(null);

  const getQuestCompleted = useCallback(
    (logs?: QuestLog[]) => {
      const questsArr: Record<
        number,
        { logId: number | undefined; isCompleted: boolean }
      > = {};

      if (questData) {
        const organizerQuestIds = new Set(
          questData.gooseGameQuestsByType.map((quest) => quest.id)
        );

        for (const quest of questData.gooseGameQuestsByType) {
          questsArr[quest.id] = { logId: undefined, isCompleted: false };
        }

        if (logs) {
          for (const log of logs) {
            questsArr[log.quest_id] = {
              logId: log.id,
              isCompleted: organizerQuestIds.has(log.quest_id),
            };
          }
        }
      }
      return questsArr;
    },
    [questData]
  );

  const questLogs = useMemo(
    () => getQuestCompleted(questLogData?.gooseGameQuestLogs),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [questLogData, questData]
  );

  useEffect(() => {
    if (questData && questData.gooseGameQuestsByType) {
      const tempQuests: Quest[] = [];
      const tempCompletedQuests: Quest[] = [];

      for (const quest of questData.gooseGameQuestsByType) {
        const mappedQuest: Quest = {
          ...quest,
          type: QuestType.OrganizerApproved,
          description: quest.description ?? undefined,
          quest_event_activity_category:
            quest.quest_event_activity_category ?? undefined,
          question_string: quest.question_string ?? undefined,
          question_answer: quest.question_answer ?? undefined,
        };

        if (questLogs[quest.id]?.isCompleted) {
          tempCompletedQuests.push(mappedQuest);
        } else {
          tempQuests.push(mappedQuest);
        }
      }

      setQuests([...tempQuests, ...tempCompletedQuests]);
    }
  }, [questData, questLogs]);

  const handleConfirm = async (
    questId: number,
    markComplete: boolean,
    errorMessage: string,
    successMessage: string,
    logId?: number,
    pointsChange?: number
  ) => {
    if (markComplete) {
      try {
        await submitCompleteLog({
          variables: {
            event_attendee_id: GooseGamePlayer.event_attendee_id,
            quest_id: questId,
          },
        });
        successToast(successMessage);
      } catch (error) {
        errorToast(errorMessage);
      } finally {
        refetch();
      }
    } else {
      if (logId && pointsChange) {
        try {
          await submitIncompleteLog({
            variables: { id: logId },
          });

          await updatePlayerPoints({
            variables: {
              event_attendee_id: GooseGamePlayer.event_attendee_id,
              points_change: -pointsChange,
              note: "Adjust organizer approved quest points.",
            },
          });

          successToast(successMessage);
        } catch (error) {
          errorToast(errorMessage);
        } finally {
          refetch();
        }
      }
    }

    setConfirmModalOpen(false);
  };

  const handleClick = (quest: Quest) => {
    setActiveQuest(quest);
    setConfirmModalOpen(true);
  };

  return (
    <Container>
      {questLogs && (
        <QuestCardGrid>
          {quests.map((quest) => (
            <QuestCard
              key={quest.id}
              quest={quest}
              view="scan"
              onCompleteClick={() => handleClick(quest)}
              isApproved={questLogs[quest.id]?.isCompleted}
            />
          ))}
        </QuestCardGrid>
      )}

      {activeQuest && (
        <MarkModal
          isOpen={confirmModalOpen}
          onConfirm={() => {
            const isCompleted = questLogs[activeQuest.id]?.isCompleted ?? false;
            const points_rewarded = quests.find(
              (quest) => quest.id === activeQuest.id
            )?.points_rewarded;
            handleConfirm(
              activeQuest.id,
              !isCompleted,
              isCompleted
                ? "Unable to mark incomplete."
                : "Unable to mark complete.",
              isCompleted
                ? "Marked “Not Complete”."
                : `You successfully completed “${activeQuest.title}” for ${
                    GooseGamePlayer.event_attendee?.user.name ?? "Unknown"
                  }.`,
              questLogs[activeQuest.id]?.logId,
              points_rewarded
            );
          }}
          onCancel={() => setConfirmModalOpen(false)}
          markComplete={!questLogs[activeQuest.id]?.isCompleted}
          name={GooseGamePlayer.event_attendee?.user.name ?? "Unknown"}
          bucks={activeQuest.points_rewarded}
          quest={activeQuest.title}
        />
      )}
    </Container>
  );
};

const Container = styled.div`
  padding: 36px;
`;

const QuestCardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  overflow-x: scroll;
`;

export default QuestManagement;
